// Admin shell — Sections (with editor), Featured, Bio, Contact.

import React from "react";
import { slugify } from "./api.js";
import { Placeholder, WorkMedia, DropZone } from "./components.jsx";

export function Admin({ state, actions, route, go, toast }) {
  const tab = route.adminTab || "sections";
  const editing = route.adminEditingSection || null;
  const setTab = (t) => go(`/admin/${t}`);
  const editSection = (sid) => go(`/admin/sections/${sid}`);
  const closeEdit = () => go(`/admin/sections`);

  const counts = {
    sections: state.sections.length,
    works: state.sections.reduce((n, s) => n + s.works.length, 0),
  };

  return (
    <div className="admin">
      <aside className="admin-side">
        <div className="admin-brand" onClick={() => go("#/")} title="View site">Chris Dicker</div>
        <div className={"admin-tab" + (tab === "sections" ? " active" : "")} onClick={() => setTab("sections")}>
          <span>Sections</span><span className="tab-count">{counts.sections}</span>
        </div>
        <div className={"admin-tab" + (tab === "featured" ? " active" : "")} onClick={() => setTab("featured")}>
          <span>Featured</span><span className="tab-count">{counts.works}</span>
        </div>
        <div className={"admin-tab" + (tab === "bio" ? " active" : "")} onClick={() => setTab("bio")}>
          <span>About</span>
        </div>
        <div className={"admin-tab" + (tab === "contact" ? " active" : "")} onClick={() => setTab("contact")}>
          <span>Contact</span>
        </div>
        <div className={"admin-tab" + (tab === "visual" ? " active" : "")} onClick={() => setTab("visual")}>
          <span>Visual</span>
        </div>
        <div className="admin-foot">
          <div className="admin-back" onClick={() => go("#/")}>← View site</div>
        </div>
      </aside>
      <main className="admin-main">
        {tab === "sections" && !editing && <SectionsTab state={state} actions={actions} onEdit={editSection} onToast={toast} />}
        {tab === "sections" && editing && <SectionEditor sid={editing} state={state} actions={actions} onBack={closeEdit} onToast={toast} />}
        {tab === "featured" && <FeaturedTab state={state} actions={actions} />}
        {tab === "bio"      && <BioTab state={state} actions={actions} onToast={toast} />}
        {tab === "contact"  && <ContactTab state={state} actions={actions} onToast={toast} />}
        {tab === "visual"   && <VisualTab state={state} actions={actions} onToast={toast} />}
      </main>
    </div>
  );
}

function VisualTab({ state, actions, onToast }) {
  const t = state.tweaks;
  const set = (patch) => actions.setTweaks(patch);
  return (
    <>
      <div className="admin-h">
        <div><h1>Visual</h1><div className="admin-sub">Type direction, paper tone, and accent color.</div></div>
      </div>
      <div className="admin-card" style={{ maxWidth: 720 }}>
        <div className="field">
          <label>Type direction</label>
          <select value={t.direction} onChange={(e) => set({ direction: e.target.value })}>
            <option value="gallery">Gallery — Schibsted Grotesk</option>
            <option value="editorial">Editorial — Instrument Serif</option>
            <option value="museum">Museum — wide caps Archivo</option>
          </select>
        </div>
        <div className="field">
          <label>Paper</label>
          <select value={t.paper} onChange={(e) => set({ paper: e.target.value })}>
            <option value="warm">Warm</option>
            <option value="white">White</option>
            <option value="cool">Cool</option>
            <option value="dark">Dark</option>
          </select>
        </div>
        <div className="field">
          <label>Accent color</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {["#9a6a2b", "#386b54", "#7d3b3b", "#3d4a6b"].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => set({ accent: c })}
                style={{
                  width: 32, height: 32, border: t.accent === c ? "2px solid var(--ink)" : "1px solid var(--rule)",
                  background: c, cursor: "pointer", padding: 0,
                }}
                aria-label={c}
              />
            ))}
            <input type="color" value={t.accent} onChange={(e) => set({ accent: e.target.value })} style={{ marginLeft: 8 }} />
          </div>
        </div>
        <button className="btn" onClick={() => onToast("Saved")}>Save</button>
      </div>
    </>
  );
}

function SectionsTab({ state, actions, onEdit, onToast }) {
  const [dragIdx, setDragIdx] = React.useState(null);
  const onAdd = async () => {
    const name = prompt("Section title?", "New series");
    if (name === null) return;
    const id = await actions.addSection(name);
    onEdit(id);
  };
  return (
    <>
      <div className="admin-h">
        <div>
          <h1>Sections</h1>
          <div className="admin-sub">Drag to reorder. Click to edit works, drafts and cover image.</div>
        </div>
        <button className="btn" onClick={onAdd}>+ New section</button>
      </div>

      {state.sections.length === 0 ? (
        <div className="empty">No sections yet. Create one to get started.</div>
      ) : (
        <div className="sections-list">
          {state.sections.map((s, i) => {
            const cover = s.works.find((w) => w.id === s.coverWorkId) || s.works[0];
            return (
              <div
                key={s.id}
                className={"section-row" + (dragIdx === i ? " dragging" : "")}
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragEnd={() => setDragIdx(null)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragIdx !== null && dragIdx !== i) actions.reorderSections(dragIdx, i); setDragIdx(null); }}
                onClick={() => onEdit(s.id)}
              >
                <div className="handle" title="Drag to reorder">⋮⋮</div>
                <div className="sr-thumb">{cover ? <WorkMedia work={cover} fit="cover" showLabel={false} /> : <Placeholder seed={0} aspect="4/3" label="" showLabel={false} />}</div>
                <div>
                  <div className="sr-title">
                    {s.title}
                    {s.draft && <span className="badge draft">Draft</span>}
                  </div>
                  <div className="sr-sub">/{s.slug}</div>
                </div>
                <div className="sr-meta">{s.works.length} {s.works.length === 1 ? "work" : "works"}</div>
                <div onClick={(e) => e.stopPropagation()}>
                  <button className="btn ghost small" onClick={() => {
                    if (!confirm(`Delete section "${s.title}" and all its works?`)) return;
                    actions.removeSection(s.id);
                    onToast("Section deleted");
                  }}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function SectionEditor({ sid, state, actions, onBack, onToast }) {
  const sec = state.sections.find((s) => s.id === sid);
  const [editingWork, setEditingWork] = React.useState(null);
  const [dragIdx, setDragIdx] = React.useState(null);
  if (!sec) return <div><a onClick={onBack} style={{ cursor: "pointer" }}>← Sections</a><div className="empty">Section not found</div></div>;

  const onUpload = async (files) => {
    for (const f of files) {
      await actions.addWork(sec.id, f, {});
    }
    onToast(`${files.length} image${files.length > 1 ? "s" : ""} added`);
  };

  return (
    <>
      <div className="admin-h">
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 6, cursor: "pointer" }} onClick={onBack}>← Sections</div>
          <h1>{sec.title}</h1>
          <div className="admin-sub">{sec.works.length} work{sec.works.length === 1 ? "" : "s"} · {sec.draft ? "Draft (hidden from site)" : "Published"}</div>
        </div>
        <button className="btn ghost" onClick={onBack}>Done</button>
      </div>

      <div className="editor-grid">
        <div>
          <div className="admin-card">
            <h3>Section details</h3>
            <div className="field">
              <label>Title</label>
              <input value={sec.title} onChange={(e) => actions.updateSection(sec.id, { title: e.target.value })} />
            </div>
            <div className="field">
              <label>URL slug</label>
              <input value={sec.slug} onChange={(e) => actions.updateSection(sec.id, { slug: slugify(e.target.value) })} />
            </div>
            <div className="field">
              <label>Blurb (shown under title)</label>
              <textarea value={sec.blurb} onChange={(e) => actions.updateSection(sec.id, { blurb: e.target.value })} placeholder="A short description…" />
            </div>
            <div className="field" style={{ flexDirection: "row", alignItems: "center", gap: 10, marginTop: 8 }}>
              <input
                id={`draft-${sec.id}`}
                type="checkbox" checked={!!sec.draft}
                onChange={(e) => actions.updateSection(sec.id, { draft: e.target.checked })}
                style={{ width: "auto" }}
              />
              <label htmlFor={`draft-${sec.id}`} style={{ margin: 0, letterSpacing: "0.04em", textTransform: "none", color: "var(--ink)" }}>Draft — hide this section from the public site</label>
            </div>
          </div>
          <div className="admin-card">
            <h3>Cover image</h3>
            <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--ink-3)" }}>Used as the thumbnail on the Portfolio page.</p>
            {sec.works.length === 0
              ? <div className="empty" style={{ padding: "24px 12px" }}>Add a work first.</div>
              : <select value={sec.coverWorkId || ""} onChange={(e) => actions.setCover(sec.id, e.target.value)} style={{ width: "100%", padding: "9px 11px", border: "1px solid var(--rule)", background: "var(--paper-2)", font: "inherit" }}>
                  {sec.works.map((w) => <option key={w.id} value={w.id}>{w.title}</option>)}
                </select>}
          </div>
        </div>

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 14, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--ink-2)" }}>Works · {sec.works.length}</h3>
            <span style={{ fontSize: 11, color: "var(--ink-3)" }}>Drag to reorder · click to edit</span>
          </div>
          <div className="works-grid">
            {sec.works.map((w, i) => (
              <div
                key={w.id}
                className={"work-cell" + (dragIdx === i ? " dragging" : "")}
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragEnd={() => setDragIdx(null)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragIdx !== null && dragIdx !== i) actions.reorderWorks(sec.id, dragIdx, i); setDragIdx(null); }}
                onClick={() => setEditingWork(w.id)}
              >
                {sec.coverWorkId === w.id && <div className="wc-cover-flag">Cover</div>}
                {state.featuredWorkId === w.id && <div className="wc-feat-flag">Featured</div>}
                <div className="wc-thumb">
                  <WorkMedia work={w} fit="cover" showLabel={false} />
                </div>
                <div className="wc-info">
                  <div className="wc-title">{w.title}</div>
                  <div className="wc-sub">{[w.year, w.medium].filter(Boolean).join(" · ") || "—"}</div>
                </div>
              </div>
            ))}
            <DropZone onFiles={onUpload} label="+ Drop or click to add" />
          </div>
        </div>
      </div>

      {editingWork && (
        <WorkDrawer
          sid={sec.id}
          wid={editingWork}
          state={state}
          actions={actions}
          onClose={() => setEditingWork(null)}
          onToast={onToast}
        />
      )}
    </>
  );
}

function WorkDrawer({ sid, wid, state, actions, onClose, onToast }) {
  const sec = state.sections.find((s) => s.id === sid);
  const w = sec?.works.find((x) => x.id === wid);
  if (!w) return null;

  const update = (patch) => actions.updateWork(sid, wid, patch);
  const replace = async (files) => {
    if (!files?.length) return;
    await actions.updateWork(sid, wid, {}, files[0]);
    onToast("Image replaced");
  };

  return (
    <div className="drawer-back" onClick={(e) => { if (e.target.classList.contains("drawer-back")) onClose(); }}>
      <div className="drawer">
        <div className="drawer-head">
          <h2>Edit work</h2>
          <button className="lb-x" style={{ color: "var(--ink-2)" }} onClick={onClose}>×</button>
        </div>
        <div className="preview">
          {w.image?.kind === "upload"
            ? <WorkMedia work={w} fit="contain" showLabel={false} />
            : <div style={{ width: 320, aspectRatio: w.image?.aspect || "4/3", display: "flex" }}>
                <Placeholder seed={w.image?.seed || 0} aspect={w.image?.aspect || "4/3"} label={w.image?.label || "painting"} />
              </div>}
        </div>
        <DropZone onFiles={replace} label="Replace image" />

        <div style={{ height: 18 }} />
        <div className="field">
          <label>Title</label>
          <input value={w.title} onChange={(e) => update({ title: e.target.value })} />
        </div>
        <div className="field-row">
          <div className="field">
            <label>Year</label>
            <input value={w.year || ""} onChange={(e) => update({ year: e.target.value })} />
          </div>
          <div className="field" style={{ gridColumn: "span 2" }}>
            <label>Medium</label>
            <input value={w.medium || ""} onChange={(e) => update({ medium: e.target.value })} />
          </div>
        </div>
        <div className="field">
          <label>Dimensions</label>
          <input value={w.dimensions || ""} onChange={(e) => update({ dimensions: e.target.value })} placeholder="e.g. 24 × 36 in" />
        </div>
        <div className="field">
          <label>Caption</label>
          <textarea value={w.caption || ""} onChange={(e) => update({ caption: e.target.value })} placeholder="Optional note for the lightbox" style={{ minHeight: 80 }} />
        </div>

        <div className="drawer-actions">
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn ghost small" onClick={() => { actions.setCover(sid, wid); onToast("Set as cover"); }}>Set as cover</button>
            <button className="btn ghost small" onClick={() => { actions.setFeatured(wid); onToast("Set as homepage featured"); }}>Feature on home</button>
          </div>
          <button className="btn danger" onClick={() => {
            if (!confirm(`Delete "${w.title}"?`)) return;
            actions.removeWork(sid, wid);
            onClose();
            onToast("Work deleted");
          }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

function FeaturedTab({ state, actions }) {
  const allWorks = state.sections.flatMap((s) => s.works.map((w) => ({ ...w, _sec: s })));
  return (
    <>
      <div className="admin-h">
        <div>
          <h1>Featured</h1>
          <div className="admin-sub">Choose the painting shown full-bleed on the homepage.</div>
        </div>
      </div>
      {allWorks.length === 0 ? (
        <div className="empty">No works yet.</div>
      ) : (
        <div className="featured-grid">
          {allWorks.map((w) => (
            <div
              key={w.id}
              className={"featured-cell" + (state.featuredWorkId === w.id ? " is-featured" : "")}
              onClick={() => actions.setFeatured(w.id)}
            >
              <WorkMedia work={w} fit="cover" showLabel={false} />
              {state.featuredWorkId === w.id && <div className="fc-check">✓</div>}
              <div className="fc-cap">{w.title} · {w._sec.title}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function BioTab({ state, actions, onToast }) {
  return (
    <>
      <div className="admin-h">
        <div><h1>About</h1><div className="admin-sub">The artist statement and bio.</div></div>
      </div>
      <div className="admin-card" style={{ maxWidth: 720 }}>
        <div className="field">
          <label>Headline</label>
          <input value={state.bio.headline} onChange={(e) => actions.setBio({ headline: e.target.value })} />
        </div>
        <div className="field">
          <label>Location / subhead</label>
          <input value={state.bio.location} onChange={(e) => actions.setBio({ location: e.target.value })} />
        </div>
        <div className="field">
          <label>Body — separate paragraphs with a blank line</label>
          <textarea value={state.bio.body} rows={14} onChange={(e) => actions.setBio({ body: e.target.value })} />
        </div>
        <button className="btn" onClick={() => onToast("Saved")}>Save</button>
      </div>
    </>
  );
}

function ContactTab({ state, actions, onToast }) {
  const c = state.contact;
  return (
    <>
      <div className="admin-h">
        <div><h1>Contact</h1><div className="admin-sub">Public contact details.</div></div>
      </div>
      <div className="admin-card" style={{ maxWidth: 720 }}>
        <div className="field"><label>Email</label><input value={c.email} onChange={(e) => actions.setContact({ email: e.target.value })} /></div>
        <div className="field"><label>Inquiries intro</label><textarea value={c.inquiries} onChange={(e) => actions.setContact({ inquiries: e.target.value })} rows={2} /></div>
        <div className="field"><label>Representation</label><input value={c.representation} onChange={(e) => actions.setContact({ representation: e.target.value })} /></div>
        <div className="field"><label>Instagram</label><input value={c.instagram} onChange={(e) => actions.setContact({ instagram: e.target.value })} /></div>
        <div className="field"><label>Studio address</label><input value={c.address} onChange={(e) => actions.setContact({ address: e.target.value })} /></div>
        <button className="btn" onClick={() => onToast("Saved")}>Save</button>
      </div>
    </>
  );
}
